import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Download, CheckCircle2, ScrollText, Eye, Lock, Mail } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { StatusPill, PositionChip } from "@/components/StatusPill";
import { viewProtected, downloadProtected } from "@/lib/files";
import api, { API, apiError } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtAmount = (a) => {
  if (a == null) return "—";
  const hasCents = Math.round(a * 100) % 100 !== 0;
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2, useGrouping: "always" }).format(a);
};
const DOC_LABEL = { demanda: "Demanda", contestacion: "Contestación", adicional: "Documentación adicional", sentencia: "Sentencia / resolución", prepared: "Versión preparada por DDP" };

const RESULT_FIELDS = [
  { k: "recommended_decision", l: "Decisión recomendada", area: true },
  { k: "claimed_amount", l: "Cuantía reclamada" },
  { k: "central_result", l: "Resultado central" },
  { k: "useful_band", l: "Banda útil" },
  { k: "main_risk", l: "Riesgo principal" },
  { k: "observations", l: "Observaciones clave", area: true },
];

export default function AdminAsuntoDetalle() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [nf, setNf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [form, setForm] = useState({ position: "" });

  const load = useCallback(() => {
    api.get(`/admin/cases/${id}`).then(({ data }) => {
      setC(data);
      if (data.result) setForm({ position: data.result.position || "", ...Object.fromEntries(RESULT_FIELDS.map((f) => [f.k, data.result[f.k] || ""])) });
    }).catch(() => setNf(true));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (nf) return <AppLayout variant="admin"><div className="ddp-card" style={{ padding: 40, textAlign: "center" }}>Asunto no encontrado. <Link to="/admin/asuntos">Volver</Link></div></AppLayout>;
  if (!c) return <AppLayout variant="admin"><div style={{ padding: 40, color: "var(--muted)" }}>Cargando…</div></AppLayout>;

  const terminado = c.status === "terminado";
  const enAnalisis = c.status === "en_analisis";
  const recibido = c.status === "recibido";

  const accept = async () => {
    setBusy(true);
    try { await api.post(`/admin/cases/${id}/accept`); toast.success("Análisis aceptado."); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const saveResult = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/cases/${id}/result`, { position: form.position || null, ...Object.fromEntries(RESULT_FIELDS.map((f) => [f.k, form[f.k] || null])) });
      toast.success("Resultado guardado.");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const uploadPdf = async () => {
    if (!pdf) return toast.error("Seleccione un PDF.");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", pdf);
      await api.post(`/admin/cases/${id}/result/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Hoja de Pronóstico subida."); setPdf(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const finalize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/cases/${id}/finalize`);
      if (data?.notification_sent) toast.success("Análisis finalizado. Despacho notificado.");
      else toast.warning("Análisis finalizado, pero no se pudo enviar la notificación al despacho.");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const resendNotification = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/cases/${id}/resend-notification`);
      if (data?.notification_sent) toast.success("Notificación reenviada al despacho.");
      else toast.warning("No se pudo enviar la notificación al despacho.");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const dl = async (f) => {
    try { await downloadProtected(`/cases/${id}/files/${f.id}/download`, f.filename); }
    catch (e) { toast.error("No se pudo descargar el documento."); }
  };

  return (
    <AppLayout variant="admin">
      <Link to="/admin/asuntos" style={{ display: "inline-flex", alignItems: "center", gap: 7, color: "var(--muted)", textDecoration: "none", fontSize: 14, fontWeight: 600 }}>
        <ArrowLeft size={16} /> Volver a asuntos
      </Link>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginTop: 18 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span className="ddp-ref" style={{ fontSize: 15 }}>{c.reference}</span>
            <StatusPill status={c.status_display} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>{c.type === "demo" ? "Caso cerrado" : "Asunto en curso"}</span>
          </div>
          <h1 className="ddp-serif" style={{ fontSize: "clamp(26px,3.6vw,36px)", color: "var(--burgundy-dark)", margin: "10px 0 0", letterSpacing: "-.02em" }}>{c.title}</h1>
        </div>
        {recibido && <button onClick={accept} disabled={busy} data-testid="accept-analysis" className="ddp-btn ddp-btn-primary">Aceptar análisis</button>}
      </div>

      <AdminPrep c={c} caseId={id} reload={load} />

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 320px", gap: 28, marginTop: 26, alignItems: "start" }} className="na-grid">
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {/* Case data */}
          <div className="ddp-card" style={{ padding: 26 }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 18 }}>Datos del expediente</div>
            <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "18px 24px", margin: 0 }}>
              <F l="Despacho" v={c.firm_name} />
              <F l="Usuario" v={c.user_name} />
              <F l="Email" v={c.user_email} />
              <F l="Parte representada" v={c.represented_party === "actora" ? "Actora" : "Demandada"} />
              <F l="Cuantía" v={fmtAmount(c.amount)} />
              <F l="Fecha" v={fmtDate(c.created_at)} />
            </dl>
            <div className="ddp-hr" style={{ margin: "20px 0" }} />
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>Problema planteado</div>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: "#3D3834", margin: 0, whiteSpace: "pre-wrap" }}>{c.question}</p>
          </div>

          {/* Result editing (en_analisis) */}
          {enAnalisis && (
            <>
              <div className="ddp-card" style={{ padding: 26 }}>
                <div className="ddp-serif" style={{ fontSize: 20, color: "var(--burgundy-dark)", marginBottom: 4 }}>Subir Hoja DDP</div>
                <p style={{ fontSize: 13.5, color: "var(--muted)", margin: "0 0 16px" }}>El análisis DDP se realiza fuera de la plataforma. Suba aquí la Hoja de Pronóstico final (PDF).</p>
                <PdfPick file={pdf} onChange={setPdf} existing={c.result?.has_pdf} />
                <button onClick={uploadPdf} disabled={busy || !pdf} data-testid="upload-pdf" className="ddp-btn ddp-btn-secondary ddp-btn-sm" style={{ marginTop: 14 }}>
                  <ScrollText size={15} /> Subir Hoja de Pronóstico
                </button>
              </div>

              <div className="ddp-card" style={{ padding: 26 }}>
                <div className="ddp-serif" style={{ fontSize: 20, color: "var(--burgundy-dark)", marginBottom: 4 }}>Resultado estructurado <span style={{ fontSize: 13, color: "var(--muted)", fontWeight: 400 }} className="ddp-sans">(opcional)</span></div>
                <p style={{ fontSize: 13.5, color: "var(--muted)", margin: "0 0 18px" }}>Campos opcionales que enriquecen la pantalla de resultado del despacho.</p>
                <label className="ddp-label">Posición</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                  {["Verde", "Ámbar", "Rojo"].map((p) => (
                    <button key={p} type="button" data-testid={`position-${p}`} onClick={() => setForm((f) => ({ ...f, position: p }))}
                      className={`pos-chip ${p === "Verde" ? "pos-verde" : p === "Ámbar" ? "pos-ambar" : "pos-rojo"}`}
                      style={{ opacity: form.position === p ? 1 : .4, border: form.position === p ? "2px solid var(--ink)" : "2px solid transparent", cursor: "pointer" }}>{p}</button>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} className="demo-cols">
                  {RESULT_FIELDS.map((f) => (
                    <div key={f.k} style={{ gridColumn: f.area ? "1 / -1" : "auto" }}>
                      <label className="ddp-label">{f.l}</label>
                      {f.area
                        ? <textarea className="ddp-textarea" style={{ minHeight: 80 }} data-testid={`rf-${f.k}`} value={form[f.k] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))} />
                        : <input className="ddp-input" data-testid={`rf-${f.k}`} value={form[f.k] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))} />}
                    </div>
                  ))}
                </div>
                <button onClick={saveResult} disabled={busy} data-testid="save-result" className="ddp-btn ddp-btn-ghost ddp-btn-sm" style={{ marginTop: 18 }}>Guardar resultado</button>
              </div>

              <div style={{ background: "#F3E7E8", border: "1px solid #E3CBCF", borderRadius: 8, padding: "20px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                <div style={{ fontSize: 14, color: "var(--burgundy-dark)", maxWidth: 420, lineHeight: 1.5 }}>
                  Al finalizar, el resultado queda cerrado (inmutable) y el despacho es notificado. Requiere la Hoja de Pronóstico (PDF).
                </div>
                <button onClick={finalize} disabled={busy} data-testid="finalize-analysis" className="ddp-btn ddp-btn-primary">FINALIZAR ANÁLISIS</button>
              </div>
            </>
          )}

          {/* Terminado — read only */}
          {terminado && (
            <div className="ddp-card" style={{ padding: 26 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <Lock size={16} style={{ color: "var(--burgundy)" }} />
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)" }}>Resultado entregado · inmutable</span>
              </div>
              {c.result?.position && <div style={{ marginBottom: 16 }}><PositionChip position={c.result.position} /></div>}
              {c.result?.recommended_decision && <div className="ddp-serif" style={{ fontSize: 20, marginBottom: 16 }}>{c.result.recommended_decision}</div>}
              <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: "16px 20px", margin: 0 }}>
                <F l="Reclamado" v={c.result?.claimed_amount} />
                <F l="Resultado central" v={c.result?.central_result} />
                <F l="Banda útil" v={c.result?.useful_band} />
                <F l="Riesgo principal" v={c.result?.main_risk} />
              </dl>
              {c.result?.has_pdf && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 20 }}>
                  <button onClick={() => viewProtected(`/cases/${id}/result/pdf`).catch(() => toast.error("No se pudo abrir la Hoja de Pronóstico."))} data-testid="admin-view-pdf" className="ddp-btn ddp-btn-secondary ddp-btn-sm">
                    <Eye size={15} /> Ver Hoja de Pronóstico
                  </button>
                  <button onClick={() => downloadProtected(`/cases/${id}/result/pdf`, `Hoja_DDP_${c.reference}.pdf`).catch(() => toast.error("No se pudo descargar."))} data-testid="admin-download-pdf" className="ddp-btn ddp-btn-ghost ddp-btn-sm">
                    <Download size={15} /> Descargar PDF
                  </button>
                </div>
              )}
              <div style={{ marginTop: 20, background: "var(--paper-2)", border: "1px solid var(--line)", borderRadius: 6, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontSize: 13, color: "var(--muted)" }}>
                  Notificación al despacho:{" "}
                  <strong style={{ color: c.notification_status === "sent" ? "var(--green)" : c.notification_status === "failed" ? "var(--red)" : "var(--muted)" }}>
                    {c.notification_status === "sent" ? "Enviada" : c.notification_status === "failed" ? "No enviada" : "—"}
                  </strong>
                  {c.notification_sent_at && <span> · {fmtDate(c.notification_sent_at)}</span>}
                </div>
                <button onClick={resendNotification} disabled={busy} data-testid="resend-notification" className="ddp-btn ddp-btn-secondary ddp-btn-sm">
                  <Mail size={15} /> Reenviar notificación
                </button>
              </div>
              {c.real_outcome && (
                <div style={{ marginTop: 20, background: "var(--paper-2)", border: "1px solid var(--line)", borderRadius: 6, padding: "14px 16px" }}>
                  <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--muted)", fontWeight: 700, marginBottom: 8 }}>Resultado real (contraste)</div>
                  <div style={{ fontSize: 14 }}><strong>Resultado:</strong> {c.real_outcome.outcome}</div>
                  {c.real_outcome.awarded_amount && <div style={{ fontSize: 14, marginTop: 4 }}><strong>Concedido:</strong> {c.real_outcome.awarded_amount}</div>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Documents sidebar */}
        <aside style={{ position: "sticky", top: 24 }}>
          <div className="ddp-card" style={{ padding: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 14 }}>Documentación</div>
            {(!c.files || c.files.length === 0) ? <div style={{ fontSize: 13.5, color: "var(--muted)" }}>Sin documentos.</div> : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {c.files.map((f) => (
                  <button key={f.id} onClick={() => dl(f)} data-testid={`admin-file-${f.id}`} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--paper-2)", border: "1px solid var(--line)", borderRadius: 5, padding: "10px 12px", cursor: "pointer", textAlign: "left" }}>
                    <FileText size={16} style={{ color: "var(--burgundy)", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.filename}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{DOC_LABEL[f.type] || f.type}</div>
                    </div>
                    <Download size={15} style={{ color: "var(--muted)" }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
    </AppLayout>
  );
}

const PREP_STATUS_LABEL = {
  created: "Creado", awaiting_ddp_preparation: "Pendiente de preparación por DDP",
  prepared_ready_review: "Versión preparada · pendiente de revisión del despacho",
  incident_open: "Incidencia abierta", prepared_approved: "Versión preparada aprobada",
  firm_prepared_submitted: "Preparada por el despacho", returned_for_correction: "Devuelta para corrección",
};

function AdminPrep({ c, caseId, reload }) {
  const [busy, setBusy] = useState(false);
  const [prep, setPrep] = useState(null);
  const [agr, setAgr] = useState(null);

  useEffect(() => {
    if (c.firm_id) api.get(`/admin/firms/${c.firm_id}/agreement`).then(({ data }) => setAgr(data)).catch(() => setAgr(null));
  }, [c.firm_id]);

  const uploadPrepared = async () => {
    if (!prep) return toast.error("Seleccione un PDF/DOCX preparado.");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", prep);
      await api.post(`/admin/cases/${caseId}/prepared`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Versión preparada enviada al despacho para revisión."); setPrep(null); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const returnCorrection = async () => {
    const reason = window.prompt("Motivo concreto de la devolución para corrección:");
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/cases/${caseId}/return-correction`, { reason }); toast.success("Documentación devuelta para corrección."); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const isDdp = c.preparation_mode === "ddp";
  const ps = c.preparation_status;
  return (
    <div className="ddp-card" data-testid="admin-prep" style={{ padding: 20, marginTop: 18 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "space-between" }}>
        <div style={{ fontSize: 13 }}>
          <div><strong>Preparación:</strong> {isDdp ? "Previa por DDP" : "Por el despacho"}</div>
          <div style={{ color: "var(--muted)", marginTop: 3 }}>Estado: {PREP_STATUS_LABEL[ps] || ps || "—"}</div>
          <div style={{ color: "var(--muted)", marginTop: 3 }} data-testid="admin-agreement">Acuerdo v{agr?.version || "1.0"}: {agr?.accepted ? `Aceptado · ${agr?.electronic_ref || ""}` : "No aceptado"}</div>
        </div>
        {agr?.accepted && (
          <button onClick={() => downloadProtected(`/admin/firms/${c.firm_id}/agreement/copy`, `Acuerdo_${c.firm_name || "despacho"}.pdf`).catch(() => toast.error("No disponible."))} data-testid="admin-agreement-copy" className="ddp-btn ddp-btn-ghost ddp-btn-sm"><Download size={14} /> Justificante</button>
        )}
      </div>
      {isDdp && c.status !== "terminado" && (
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input type="file" data-testid="admin-prepared-file" accept=".pdf,.docx,.txt" onChange={(e) => setPrep(e.target.files?.[0] || null)} />
          <button onClick={uploadPrepared} disabled={busy} data-testid="admin-upload-prepared" className="ddp-btn ddp-btn-primary ddp-btn-sm">Subir versión preparada</button>
        </div>
      )}
      {!isDdp && c.status === "recibido" && (
        <div style={{ marginTop: 14 }}>
          <button onClick={returnCorrection} disabled={busy} data-testid="admin-return-correction" className="ddp-btn ddp-btn-secondary ddp-btn-sm">Devolver para corrección</button>
        </div>
      )}
    </div>
  );
}

function F({ l, v }) {
  return (
    <div>
      <dt style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--muted)", fontWeight: 700, marginBottom: 5 }}>{l}</dt>
      <dd style={{ margin: 0, fontSize: 15, color: "var(--ink)", overflowWrap: "anywhere", wordBreak: "break-word" }}>{v || "—"}</dd>
    </div>
  );
}

function PdfPick({ file, onChange, existing }) {
  return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 12, border: "1.5px dashed var(--line)", background: "var(--paper-2)", borderRadius: 7, padding: "16px 18px", cursor: "pointer" }}>
        <ScrollText size={20} style={{ color: "var(--burgundy)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>{file ? file.name : existing ? "Hoja de Pronóstico cargada · sustituir" : "Seleccionar PDF de la Hoja de Pronóstico"}</div>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>PDF · Máx. 25 MB</div>
        </div>
        <input type="file" accept=".pdf" data-testid="pdf-input" style={{ display: "none" }} onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </label>
    </div>
  );
}
